import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { showToast } from '../../features/ui/uiSlice';
import { getUserApplications, applyToTask } from '../../features/taskApplications/taskApplicationSlice';
import {
    ClipboardDocumentListIcon,
    BuildingOfficeIcon,
    EyeIcon,
    PaperAirplaneIcon,
    MapPinIcon,
    CalendarIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';

interface Task {
    _id: string;
    title: string;
    description: string;
    taskUrl: string;
    totalBudget: number;
    status: string;
    regions?: string[];
    startDate?: string;
    endDate?: string;
    requiredSkills?: Array<{
        _id: string;
        name: string;
    }>;
    company: {
        _id: string;
        companyName: string;
        logo?: string;
    };
}


const TaskApplications: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { userApplications, loading } = useAppSelector((state) => state.taskApplications);
    const [activeTab, setActiveTab] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);
    const [applicationMessage, setApplicationMessage] = useState('');
    const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
    const [selectedTaskForDetails, setSelectedTaskForDetails] = useState<Task | null>(null);

    const fetchApplications = useCallback(async () => {
        try {
            await dispatch(getUserApplications({
                status: activeTab === 'all' ? undefined : activeTab,
                page: currentPage,
                limit: 10
            }));
        } catch (error) {
            console.error('Error fetching applications:', error);
        }
    }, [activeTab, currentPage, dispatch]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    const handleApplyToTask = async () => {
        if (!selectedTask) return;

        try {
            await dispatch(applyToTask({
                taskId: selectedTask._id,
                applicationMessage: applicationMessage.trim() || undefined
            })).unwrap();

            dispatch(showToast({
                message: 'Application submitted successfully!',
                type: 'success'
            }));

            setShowApplicationModal(false);
            setSelectedTask(null);
            setApplicationMessage('');
            fetchApplications();
        } catch (error: any) {
            dispatch(showToast({
                message: error.message || 'Failed to submit application',
                type: 'error'
            }));
        }
    };

    const openApplicationModal = (task: Task) => {
        setSelectedTask(task);
        setShowApplicationModal(true);
    };

    const openTaskDetailsModal = (task: Task) => {
        setSelectedTaskForDetails(task);
        setShowTaskDetailsModal(true);
    };


    const getStatusColor = (status: string) => {
        switch (status) {
            case 'invited':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
            case 'applied':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
            case 'approved':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'invited':
                return 'Invited';
            case 'applied':
                return 'Applied';
            case 'approved':
                return 'Approved';
            case 'rejected':
                return 'Rejected';
            default:
                return 'Unknown';
        }
    };

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'Not specified';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 container mx-auto px-4 py-8">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Task Applications
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                            Manage your task applications and invitations
                        </p>
                    </div>
                    <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="flex space-x-8 px-6">
                        {[
                            { key: 'all', label: 'All Applications' },
                            { key: 'invited', label: 'Invitations' },
                            { key: 'applied', label: 'Applied' },
                            { key: 'approved', label: 'Approved' },
                            { key: 'rejected', label: 'Rejected' }
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => {
                                    setActiveTab(tab.key);
                                    setCurrentPage(1);
                                }}
                                className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.key
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Applications List */}
                <div className="p-6">
                    {userApplications.length === 0 ? (
                        <div className="text-center py-12">
                            <ClipboardDocumentListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                                No applications found
                            </h3>
                            <p className="text-gray-500 dark:text-gray-400">
                                {activeTab === 'all'
                                    ? "You haven't applied to any tasks yet."
                                    : `No ${activeTab} applications found.`
                                }
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {userApplications.filter(application => application.task).map((application) => (
                                <div
                                    key={application._id}
                                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600"
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                    {application.task?.title || 'Task Deleted'}
                                                </h3>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                                                    {getStatusText(application.status)}
                                                </span>
                                            </div>

                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                                {application.task?.company?.companyName || 'Unknown Company'}
                                            </p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                                {application.task?.description || 'No description available'}
                                            </p>

                                            <div className="flex items-center space-x-4 mb-3">
                                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                    <MapPinIcon className="h-4 w-4 mr-1" />
                                                    {(application.task as any)?.regions && (application.task as any).regions.length > 0 ? (application.task as any).regions.join(', ') : 'No regions'}
                                                </div>
                                                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                                    <CalendarIcon className="h-4 w-4 mr-1" />
                                                    {(application.task as any)?.startDate ? formatDate((application.task as any).startDate) : 'N/A'} - {(application.task as any)?.endDate ? formatDate((application.task as any).endDate) : 'N/A'}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Applied On</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {formatDate(application.appliedAt)}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Review Status</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {application.reviewedAt ? `Reviewed ${formatDate(application.reviewedAt)}` : 'Pending review'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">Application Status</p>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                                        {application.status}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="mb-4">
                                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Required Skills</p>
                                                <div className="flex flex-wrap gap-1">
                                                    {(application.task as any)?.requiredSkills && (application.task as any).requiredSkills.length > 0 ? (
                                                        (application.task as any).requiredSkills.map((skill: { _id: string; name: string }) => (
                                                            <span key={skill._id} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                                                                {skill.name}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="px-2 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 rounded text-xs">
                                                            No skills specified
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {application.applicationMessage && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Application Message</p>
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                                                        {application.applicationMessage}
                                                    </p>
                                                </div>
                                            )}

                                            {application.rejectionReason && (
                                                <div className="mb-4">
                                                    <p className="text-sm text-red-500 dark:text-red-400 mb-2">Rejection Reason</p>
                                                    <p className="text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                                        {application.rejectionReason}
                                                    </p>
                                                </div>
                                            )}

                                            <div className="mt-4 flex justify-between items-center">
                                                <div className="flex items-center space-x-4">
                                                    {application.status === 'approved' && application.assignedDesignation && (
                                                        <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                                                            ✓ Assigned as {application.assignedDesignation.replace('_', ' ')}
                                                        </span>
                                                    )}
                                                    {application.status === 'rejected' && (
                                                        <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                                                            ✗ Application rejected
                                                        </span>
                                                    )}
                                                    {application.status === 'applied' && (
                                                        <span className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                                                            ⏳ Under review
                                                        </span>
                                                    )}
                                                    {application.status === 'invited' && (
                                                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                                            📧 You've been invited to apply
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    {application.status === 'approved' && application.task && (
                                                        <button
                                                            onClick={() => navigate(`/dashboard/tasks/${application.task._id}`)}
                                                            className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-2" />
                                                            View Details
                                                        </button>
                                                    )}
                                                    {application.status === 'invited' && application.task && (
                                                        <button
                                                            onClick={() => openApplicationModal(application.task)}
                                                            className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                        >
                                                            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                                                            Apply Now
                                                        </button>
                                                    )}
                                                    {application.status !== 'approved' && application.status !== 'invited' && (
                                                        <button
                                                            onClick={() => openTaskDetailsModal(application.task)}
                                                            className="inline-flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                                        >
                                                            <EyeIcon className="h-4 w-4 mr-2" />
                                                            View Task
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Task Details Modal */}
            {showTaskDetailsModal && selectedTaskForDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Task Details
                            </h2>
                            <button
                                onClick={() => {
                                    setShowTaskDetailsModal(false);
                                    setSelectedTaskForDetails(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Task Header */}
                            <div className="bg-gray-50 dark:bg-gray-900 p-6 rounded-lg">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    {selectedTaskForDetails.title}
                                </h3>
                                <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                                    {selectedTaskForDetails.company?.companyName || 'Unknown Company'}
                                </p>
                                <div className="flex items-center space-x-6">
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <MapPinIcon className="h-4 w-4 mr-1" />
                                        {(selectedTaskForDetails as any).regions && (selectedTaskForDetails as any).regions.length > 0 ? (selectedTaskForDetails as any).regions.join(', ') : 'No regions'}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <CalendarIcon className="h-4 w-4 mr-1" />
                                        {formatDate((selectedTaskForDetails as any).startDate)} - {formatDate((selectedTaskForDetails as any).endDate)}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <ClipboardDocumentListIcon className="h-4 w-4 mr-1" />
                                        Status: {selectedTaskForDetails.status}
                                    </div>
                                </div>
                            </div>

                            {/* Task Description */}
                            <div>
                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                    Task Description
                                </h4>
                                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                    {selectedTaskForDetails.description || 'No description available for this task.'}
                                </p>
                            </div>

                            {/* Required Skills */}
                            {(selectedTaskForDetails as any).requiredSkills && (selectedTaskForDetails as any).requiredSkills.length > 0 && (
                                <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                        Required Skills
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {(selectedTaskForDetails as any).requiredSkills.map((skill: { _id: string; name: string }) => (
                                            <span key={skill._id} className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm font-medium">
                                                {skill.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Task Information Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Task Information</h5>
                                    <div className="space-y-2 text-sm">
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Task ID:</span>
                                            <span className="ml-2 text-gray-900 dark:text-white font-mono">{selectedTaskForDetails._id}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Status:</span>
                                            <span className="ml-2 text-gray-900 dark:text-white capitalize">{selectedTaskForDetails.status}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Company:</span>
                                            <span className="ml-2 text-gray-900 dark:text-white">{selectedTaskForDetails.company?.companyName || 'Unknown'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                    <h5 className="font-semibold text-gray-900 dark:text-white mb-2">Timeline</h5>
                                    <div className="space-y-2 text-sm">
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Start Date:</span>
                                            <span className="ml-2 text-gray-900 dark:text-white">{formatDate((selectedTaskForDetails as any).startDate)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">End Date:</span>
                                            <span className="ml-2 text-gray-900 dark:text-white">{formatDate((selectedTaskForDetails as any).endDate)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-500 dark:text-gray-400">Regions:</span>
                                            <span className="ml-2 text-gray-900 dark:text-white">
                                                {(selectedTaskForDetails as any).regions && (selectedTaskForDetails as any).regions.length > 0 ? (selectedTaskForDetails as any).regions.join(', ') : 'Not specified'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Task URL */}
                            {selectedTaskForDetails.taskUrl && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <h5 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Task Link</h5>
                                    <a
                                        href={selectedTaskForDetails.taskUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline break-all"
                                    >
                                        {selectedTaskForDetails.taskUrl}
                                    </a>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => {
                                    setShowTaskDetailsModal(false);
                                    setSelectedTaskForDetails(null);
                                }}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Application Modal */}
            {showApplicationModal && selectedTask && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Apply to Invited Task
                            </h2>
                            <button
                                onClick={() => {
                                    setShowApplicationModal(false);
                                    setSelectedTask(null);
                                    setApplicationMessage('');
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XMarkIcon className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Task Information */}
                            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                    {selectedTask.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {selectedTask.company?.companyName || 'Unknown Company'}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                    {selectedTask.description || 'No description available'}
                                </p>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <MapPinIcon className="h-4 w-4 mr-1" />
                                        {(selectedTask as any).regions && (selectedTask as any).regions.length > 0 ? (selectedTask as any).regions.join(', ') : 'No regions'}
                                    </div>
                                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                        <CalendarIcon className="h-4 w-4 mr-1" />
                                        {formatDate((selectedTask as any).startDate)} - {formatDate((selectedTask as any).endDate)}
                                    </div>
                                </div>
                                {(selectedTask as any).requiredSkills && (selectedTask as any).requiredSkills.length > 0 && (
                                    <div className="mt-3">
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Required Skills</p>
                                        <div className="flex flex-wrap gap-1">
                                            {(selectedTask as any).requiredSkills.map((skill: { _id: string; name: string }) => (
                                                <span key={skill._id} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                                                    {skill.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Application Message */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Application Message (Optional)
                                </label>
                                <textarea
                                    value={applicationMessage}
                                    onChange={(e) => setApplicationMessage(e.target.value)}
                                    placeholder="Tell us why you're interested in this task and how you can contribute..."
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    This message will be sent to the task administrator along with your application.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => {
                                    setShowApplicationModal(false);
                                    setSelectedTask(null);
                                    setApplicationMessage('');
                                }}
                                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleApplyToTask}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                Submit Application
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskApplications;
